/**
 * Next Day Delivery countdown on the product page (MFS-688).
 *
 * Shows "Order within 01 hrs 54 min for Next Day Delivery" Monday-Friday until the cutoff
 * (default 14:00), and hides it after the cutoff, at weekends, on configured bank holidays (and the
 * day before one, when "next day" would be the holiday), and when Next Day Delivery is capped.
 *
 * UK TIME, NOT THE SHOPPER'S CLOCK. The cutoff is the warehouse's, so a shopper browsing from
 * New York must see the same window as one in Leeds. Liquid's `now` is no use either: product
 * pages are cached, so the time has to be worked out in the browser.
 *
 * MINUTES ROUND UP. With 30 seconds left the banner reads "00 hrs 01 min", never "00 hrs 00 min".
 *
 * THE CAP comes from the shop metafield `engine.shipping_caps`, published by the Engine
 * (`SyncShippingCapsToShopifyJob`) and also read by the checkout function in
 * apps/shipping-volume-cap, which hides the capped method there. Shape:
 *   { "enabled": true, "updated_at": "...", "capped": [ { "code": "shqUK-NEXT-DAY_COURIER_24", "title": "UK Next Day" } ] }
 * Caps are per delivery method and there are two Next Day methods (Evri and DPD), so the banner
 * hides only once EVERY configured Next Day method is capped: while one is still offered at
 * checkout, the promise still holds.
 *
 * RE-CHECKED WHILE OPEN. The cap can be reached while a product page sits in a tab, so the
 * component re-fetches its own section (Section Rendering API) every 30 minutes and whenever the
 * tab comes back into view after that long, and re-reads the cap from the fresh HTML.
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api; // node tests
    return;
  }
  root.NddCountdown = api; // browser
})(typeof self !== 'undefined' ? self : this, function () {
  const UK_PARTS = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  function ukClock(now) {
    const parts = {};
    UK_PARTS.formatToParts(now).forEach((p) => (parts[p.type] = p.value));
    return {
      weekend: parts.weekday === 'Sat' || parts.weekday === 'Sun',
      date: parts.year + '-' + parts.month + '-' + parts.day,
      seconds: Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second),
    };
  }

  /** The UK calendar date (YYYY-MM-DD) one day after `now`. Adding 24h is safe across DST here. */
  function ukTomorrow(now) {
    return ukClock(new Date(now.getTime() + 24 * 3600 * 1000)).date;
  }

  /** Bank holidays arrive from the theme editor as free text, one YYYY-MM-DD per line. */
  function parseHolidays(holidays) {
    const list = Array.isArray(holidays) ? holidays : String(holidays || '').split(/\r?\n/);
    return list.map((d) => String(d).trim()).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
  }

  /**
   * Milliseconds until today's UK cutoff, or null on a weekend, on a bank holiday, on the day
   * before a bank holiday, or once the cutoff has passed.
   */
  function remainingMs(now, cutoffHour, cutoffMinute, holidays) {
    const uk = ukClock(now);
    if (uk.weekend) return null;
    const closed = parseHolidays(holidays);
    if (closed.includes(uk.date) || closed.includes(ukTomorrow(now))) return null;
    const ms = (cutoffHour * 3600 + cutoffMinute * 60 - uk.seconds) * 1000 - now.getMilliseconds();
    return ms > 0 ? ms : null;
  }

  function formatRemaining(ms) {
    const totalMinutes = Math.ceil(ms / 60000);
    const pad = (n) => String(n).padStart(2, '0');
    return pad(Math.floor(totalMinutes / 60)) + ' hrs ' + pad(totalMinutes % 60) + ' min';
  }

  /** Delivery method codes as published by the Engine; the default list covers Evri and DPD Next Day. */
  const DEFAULT_METHODS = ['shqUK-NEXT-DAY_COURIER_24', 'shqDPD-UK-NEXT-DAY__Day_Delivery_DPD'];

  function parseMethods(methods) {
    const list = Array.isArray(methods) ? methods : String(methods || '').split(/\r?\n/);
    const codes = list.map((m) => String(m).trim()).filter(Boolean);
    return codes.length ? codes : DEFAULT_METHODS;
  }

  /** True only when every configured Next Day method is in the capped list. */
  function isNextDayCapped(state, methods) {
    if (!state || state.enabled !== true || !Array.isArray(state.capped)) return false;
    const cappedCodes = state.capped
      .map((entry) => entry && (entry.code || entry.title))
      .filter((code) => typeof code === 'string');
    return parseMethods(methods).every((method) => cappedCodes.includes(method));
  }

  return { remainingMs, formatRemaining, isNextDayCapped, parseHolidays, parseMethods, DEFAULT_METHODS };
});

if (typeof document !== 'undefined') {
  const RECHECK_MS = 30 * 60 * 1000;

  document.addEventListener('alpine:init', () => {
    Alpine.data('xNddCountdown', (config) => ({
      visible: false,
      time: '',
      caps: null,
      capped: false,
      timer: null,
      cutoffTimer: null,
      recheckTimer: null,
      lastCheck: 0,
      init() {
        this.readCaps(this.$el);
        this.tick();
        this.timer = setInterval(() => this.tick(), 30000);
        this.recheckTimer = setInterval(() => this.recheck(), RECHECK_MS);
        this.onVisible = () => {
          if (document.visibilityState !== 'visible') return;
          this.tick(); // a backgrounded tab's timers are throttled, so catch up on the clock first
          if (Date.now() - this.lastCheck >= RECHECK_MS) this.recheck();
        };
        document.addEventListener('visibilitychange', this.onVisible);
      },
      readCaps(el) {
        try {
          this.caps = JSON.parse(el.dataset.caps || 'null');
        } catch (e) {
          this.caps = null;
        }
        this.capped = NddCountdown.isNextDayCapped(this.caps, config.methods);
        this.lastCheck = Date.now();
      },
      /**
       * The 30-second interval keeps the minutes fresh; the cutoff itself gets its own timeout so
       * the banner goes at 14:00:00, not up to 30 seconds later on the next interval.
       */
      tick() {
        const ms = this.capped
          ? null
          : NddCountdown.remainingMs(new Date(), config.cutoffHour, config.cutoffMinute, config.holidays);
        this.visible = ms !== null;
        clearTimeout(this.cutoffTimer);
        if (ms !== null) {
          this.time = NddCountdown.formatRemaining(ms);
          this.cutoffTimer = setTimeout(() => this.tick(), ms + 50);
        }
      },
      /**
       * Re-read the cap from a fresh render of this section; the page itself may be hours old.
       * On failure (offline, 5xx, markup changed) the last known cap state is kept on purpose: a
       * blip must not hide a banner the page-load check approved, and the next interval retries.
       * Checkout stays the authority either way, as its function hides capped methods itself.
       */
      recheck() {
        if (!config.url || !config.sectionId) return Promise.resolve();
        return fetch(`${config.url}?section_id=${config.sectionId}`)
          .then((res) => (res.ok ? res.text() : Promise.reject(new Error('HTTP ' + res.status))))
          .then((html) => {
            const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.x-block-ndd-countdown');
            if (!fresh) throw new Error('section has no .x-block-ndd-countdown');
            this.readCaps(fresh);
            this.tick();
          })
          .catch((err) => console.warn('[ndd-countdown] cap re-check failed, keeping last state:', err));
      },
      destroy() {
        clearInterval(this.timer);
        clearTimeout(this.cutoffTimer);
        clearInterval(this.recheckTimer);
        document.removeEventListener('visibilitychange', this.onVisible);
      },
    }));
  });
}
